import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Info } from '@mui/icons-material';
import { PlayerMatch } from '../../models/PlayerMatch';
import { Team } from '../../models/Team';
import TeamLogo from '../../components/TeamLogo';
import { Side, getSideStats, playerName } from './matchStats';
import styles from './MatchPage2.module.css';

interface Match2MatrixProps {
    team1: Team;
    team2: Team;
    team1Rows: PlayerMatch[]; // sorted; matrix rows
    team2Rows: PlayerMatch[]; // sorted; matrix columns
    side: Side;
}

interface TooltipState {
    id: string;
    left: number;
    top: number;
    placement: 'above' | 'below';
    rowName: string;
    colName: string;
    kills: number;
    deaths: number;
}

const TOOLTIP_GAP = 8;
const TOOLTIP_EST_HEIGHT = 90; // rough box height incl. padding, used only to pick above/below

// Kill matrix: rows = team1 players, columns = team2 players; cells are
// kills-deaths of the duel (side-aware via killsAgainstPlayers variants),
// colored when one player dominates by 3+, with a hover tooltip.
const Match2Matrix: React.FC<Match2MatrixProps> = ({ team1, team2, team1Rows, team2Rows, side }) => {
    const { t } = useTranslation('pages');
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const killsAgainst = (killer: PlayerMatch, victim: PlayerMatch): number =>
        getSideStats(killer, side).killsAgainst?.[victim.steamId] ?? 0;

    // Positioned via the cell's real bounding rect (portal to body) rather than
    // CSS position:absolute inside the horizontally-scrolling grid, so the tooltip
    // can never trigger that container's overflow/scrollbar or get clipped by it.
    // Flips above the cell once it would otherwise spill past the grid's own bottom
    // edge (into the caption below it) or off the bottom of the viewport.
    const showTooltip = (
        e: React.MouseEvent<HTMLDivElement>,
        id: string,
        rowP: PlayerMatch,
        colP: PlayerMatch,
        kills: number,
        deaths: number,
    ) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const gridBottom = scrollRef.current?.getBoundingClientRect().bottom ?? window.innerHeight;
        const bound = Math.min(gridBottom, window.innerHeight);
        const placement: 'above' | 'below' =
            rect.bottom + TOOLTIP_GAP + TOOLTIP_EST_HEIGHT > bound ? 'above' : 'below';
        setTooltip({
            id,
            left: rect.left + rect.width / 2,
            top: placement === 'below' ? rect.bottom + TOOLTIP_GAP : rect.top - TOOLTIP_GAP,
            placement,
            rowName: playerName(rowP),
            colName: playerName(colP),
            kills,
            deaths,
        });
    };

    const hideTooltip = (id: string) => {
        setTooltip(prev => (prev && prev.id === id ? null : prev));
    };

    return (
        <div className={styles.fadeIn} style={{ marginTop: 18 }}>
            <div className={styles.matrixCard}>
                <div className={styles.matrixHead}>
                    <div>
                        <h3 className={styles.matrixTitle}>Kill Matrix</h3>
                        <p className={styles.matrixIntro}>{t('match2.matrixIntro')}</p>
                    </div>
                    <div className={styles.matrixLegend}>
                        <span className={styles.legendItem}>
                            <span className={`${styles.legendChip} ${styles.legendChipGood}`} />
                            {t('match2.matrixLegendDominates')}
                        </span>
                        <span className={styles.legendItem}>
                            <span className={`${styles.legendChip} ${styles.legendChipBad}`} />
                            {t('match2.matrixLegendDominated')}
                        </span>
                    </div>
                </div>

                <div className={styles.matrixScroll} ref={scrollRef}>
                    <div
                        className={styles.matrixGrid}
                        style={{ gridTemplateColumns: `140px repeat(${team2Rows.length}, 62px)` }}
                    >
                        {/* Corner: team2 axis label */}
                        <div className={styles.matrixAxisLabel} style={{ gridColumn: '2 / -1' }}>
                            <TeamLogo logo={team2.teamLogo} name={team2.teamName} className={styles.matrixAxisLogo} />
                            <span className={styles.matrixAxisName}>{team2.teamName} →</span>
                        </div>
                        <div />
                        {team2Rows.map(p => (
                            <div key={p.steamId} className={styles.matrixColHead}>{playerName(p)}</div>
                        ))}
                        {team1Rows.map((rowP, ri) => (
                            <React.Fragment key={rowP.steamId}>
                                <div className={styles.matrixRowHead}>
                                    {ri === 0 ? (
                                        <TeamLogo logo={team1.teamLogo} name={team1.teamName} className={styles.matrixRowLogo} />
                                    ) : (
                                        <span className={styles.matrixRowSpacer} />
                                    )}
                                    <span className={styles.matrixRowName}>{playerName(rowP)}</span>
                                </div>
                                {team2Rows.map((colP, ci) => {
                                    const kills = killsAgainst(rowP, colP);
                                    const deaths = killsAgainst(colP, rowP);
                                    const any = kills > 0 || deaths > 0;
                                    const diff = kills - deaths;
                                    const id = `${ri}-${ci}`;
                                    let cellClass = styles.matrixCell;
                                    if (any && diff >= 3) cellClass += ` ${styles.matrixCellGood}`;
                                    else if (any && diff <= -3) cellClass += ` ${styles.matrixCellBad}`;
                                    else if (any) cellClass += ` ${styles.matrixCellAny}`;
                                    return (
                                        <div
                                            key={colP.steamId}
                                            className={cellClass}
                                            onMouseEnter={(e) => showTooltip(e, id, rowP, colP, kills, deaths)}
                                            onMouseLeave={() => hideTooltip(id)}
                                        >
                                            {any ? `${kills}-${deaths}` : '–'}
                                        </div>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className={styles.matrixFoot}>
                    <Info sx={{ fontSize: 17 }} />
                    {t('match2.matrixCaption')}
                </div>
            </div>

            {tooltip && createPortal(
                <div
                    className={`${styles.matrixTip} ${
                        tooltip.placement === 'above' ? styles.matrixTipAbove : styles.matrixTipBelow
                    }`}
                    style={{ left: tooltip.left, top: tooltip.top }}
                >
                    <div className={styles.matrixTipTitle}>
                        {tooltip.rowName} vs {tooltip.colName}
                    </div>
                    <div className={styles.matrixTipKills}>
                        {tooltip.rowName}: {tooltip.kills} kills
                    </div>
                    <div className={styles.matrixTipDeaths}>
                        {tooltip.colName}: {tooltip.deaths} kills
                    </div>
                </div>,
                document.body,
            )}
        </div>
    );
};

export default Match2Matrix;
